import type { CatalogProductItemDetails } from '../../Catalog.types';

type PdpSectionsList = NonNullable<CatalogProductItemDetails['pdpSections']>;

const normalize = (text: string | null | undefined) =>
  text
    ?.trim()
    .toLowerCase()
    .replace(/[?.!]+$/, '') ?? '';

// Whole detail sections that only make sense for physical products and should be
// hidden on cash/charity fund PDPs. Matched against the section header text because
// PDP element keys are opaque content hashes with no semantic type marker.
const PHYSICAL_PRODUCT_ONLY_SECTION_TITLES = new Set(['delivery']);

// Headings nested inside an otherwise-shared section (e.g. "Product Information")
// that only apply to physical products. The row and its body are stripped; if the
// section is left with no rows it is dropped entirely.
const PHYSICAL_PRODUCT_ONLY_HEADINGS = new Set(['prop 65', "what's in the box"]);

export const removePhysicalProductDetailSections = (sections: PdpSectionsList): PdpSectionsList =>
  sections.reduce<PdpSectionsList>((acc, section) => {
    const sectionTree = section.sectionTree;
    const rootElement = sectionTree?.elements?.find(element => element.key === sectionTree.rootElementKey);

    if (!sectionTree || !rootElement) {
      acc.push(section);
      return acc;
    }

    if (PHYSICAL_PRODUCT_ONLY_SECTION_TITLES.has(normalize(rootElement.text))) {
      return acc;
    }

    const childrenKeys = rootElement.childrenKeys.filter(key => {
      const child = sectionTree.elements.find(element => element.key === key);
      return !PHYSICAL_PRODUCT_ONLY_HEADINGS.has(normalize(child?.text));
    });

    if (childrenKeys.length === rootElement.childrenKeys.length) {
      acc.push(section);
      return acc;
    }

    if (childrenKeys.length === 0) {
      return acc;
    }

    acc.push({
      ...section,
      sectionTree: {
        ...sectionTree,
        elements: sectionTree.elements.map(element => (element.key === rootElement.key ? { ...element, childrenKeys } : element)),
      },
    });
    return acc;
  }, []);
